import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';
import { buildArticleSchema } from '../lib/seo';

const GUIDE_PATH = '/how-to-prepare-for-eaa-compliance';
const GUIDE_DATE = '2026-05-21';

interface GuideSection {
  title: string;
  body: string;
  points?: string[];
}

interface GuideStep {
  title: string;
  body: string;
}

interface GuideResource {
  href: string;
  label: string;
  description: string;
}

interface GuideAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

export default function EaaComplianceGuidePage() {
  const { t } = useTranslation('marketing');
  const attentionItems = t('pages.eaaComplianceGuide.attention.items', { returnObjects: true }) as string[];
  const checklist = t('pages.eaaComplianceGuide.checklist.items', { returnObjects: true }) as string[];
  const processSteps = t('pages.eaaComplianceGuide.process.steps', { returnObjects: true }) as GuideStep[];
  const sections = t('pages.eaaComplianceGuide.sections', { returnObjects: true }) as GuideSection[];
  const relatedResources = t('pages.eaaComplianceGuide.related.resources', { returnObjects: true }) as GuideResource[];
  const sidebarPoints = t('pages.eaaComplianceGuide.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.eaaComplianceGuide.cta.actions', { returnObjects: true }) as GuideAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.eaaComplianceGuide.meta.title')}
      metaDescription={t('pages.eaaComplianceGuide.meta.description')}
      canonicalPath={GUIDE_PATH}
      eyebrow={t('pages.eaaComplianceGuide.hero.eyebrow')}
      title={t('pages.eaaComplianceGuide.hero.title')}
      intro={t('pages.eaaComplianceGuide.hero.intro')}
      metaType="article"
      structuredData={[
        buildArticleSchema({
          headline: t('pages.eaaComplianceGuide.hero.title'),
          description: t('pages.eaaComplianceGuide.meta.description'),
          path: GUIDE_PATH,
          datePublished: GUIDE_DATE,
          dateModified: GUIDE_DATE,
        }),
      ]}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.eaaComplianceGuide.sidebar.title')}</h2>
            <p>{t('pages.eaaComplianceGuide.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.eaaComplianceGuide.sidebar.noteTitle')}</h2>
            <p>{t('pages.eaaComplianceGuide.sidebar.noteBody')}</p>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.eaaComplianceGuide.intro.title')}</h2>
        <p>{t('pages.eaaComplianceGuide.intro.body')}</p>
        <p>{t('pages.eaaComplianceGuide.intro.note')}</p>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.eaaComplianceGuide.whatIs.title')}</h2>
        <p>{t('pages.eaaComplianceGuide.whatIs.body')}</p>
        <ul className="acc-public-page-list">
          {(t('pages.eaaComplianceGuide.whatIs.points', { returnObjects: true }) as string[]).map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.eaaComplianceGuide.attention.title')}</h2>
        <p>{t('pages.eaaComplianceGuide.attention.body')}</p>
        <ul className="acc-public-page-list">
          {attentionItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.eaaComplianceGuide.checklist.title')}</h2>
        <ul className="acc-public-page-list" aria-label={t('pages.eaaComplianceGuide.checklist.ariaLabel')}>
          {checklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-section" aria-labelledby="eaa-process-title">
        <div>
          <h2 id="eaa-process-title">{t('pages.eaaComplianceGuide.process.title')}</h2>
          <p>{t('pages.eaaComplianceGuide.process.body')}</p>
        </div>

        <div className="acc-public-page-grid">
          {processSteps.map((step, index) => (
            <article key={step.title} className="acc-public-page-subcard">
              <h3>
                {t('pages.eaaComplianceGuide.process.stepLabel', { number: index + 1 })}
                {' '}
                {step.title}
              </h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      {sections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-page-section" aria-labelledby="eaa-related-title">
        <div>
          <h2 id="eaa-related-title">{t('pages.eaaComplianceGuide.related.title')}</h2>
          <p>{t('pages.eaaComplianceGuide.related.body')}</p>
        </div>
        <div className="acc-public-page-grid">
          {relatedResources.map((resource) => (
            <article key={resource.href} className="acc-public-page-subcard">
              <h3>{resource.label}</h3>
              <p>{resource.description}</p>
              <div className="acc-public-page-actions">
                <PublicCtaLink href={resource.href} variant="ghost">
                  {resource.label}
                </PublicCtaLink>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.eaaComplianceGuide.disclaimer.title')}</h2>
        <p>{t('pages.eaaComplianceGuide.disclaimer.body')}</p>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="eaa-guide-cta-title">
        <div>
          <h2 id="eaa-guide-cta-title">{t('pages.eaaComplianceGuide.cta.title')}</h2>
          <p>{t('pages.eaaComplianceGuide.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
